import { UpdateMessageMap } from './types';
import logger from '../../lib/logger';
import store from '../../store';
import { setUsage } from '../../features/plan/planSlice';
import { RootState } from '../../reducers';
import {
  fetchFileUpdate,
  updateJobStatus,
} from '../../features/selectedVideo/selectedVideoSlice';
import { updateNotification } from '../../features/dashboard/dashboardSlice';
import { getFileUrl } from '../../lib/routingUtils';
import {
  updateFile,
  triggerFileListRefresh,
} from '../../features/library/librarySlice';
import { handleRagUpdate } from './ragUpdate';

// Utility function to extract user-friendly information from URLs
const extractUserFriendlyUrlInfo = (
  url: string,
): { source: string; displayName: string } => {
  // Handle the fallback case for unknown/undefined URLs
  if (url === 'Unknown source') {
    return {
      source: 'Unknown Source',
      displayName: 'Imported content',
    };
  }

  try {
    const urlObj = new URL(url);
    const { hostname, pathname, searchParams } = urlObj;
    const lowerHostname = hostname.toLowerCase();

    // Handle known platforms
    if (
      lowerHostname.includes('youtube.com') ||
      lowerHostname.includes('youtu.be')
    ) {
      const videoId = lowerHostname.includes('youtu.be')
        ? pathname.slice(1)
        : searchParams.get('v');
      return {
        source: 'YouTube',
        displayName: videoId ? `YouTube video (${videoId})` : 'YouTube video',
      };
    }

    if (lowerHostname.includes('vimeo.com')) {
      const videoId = pathname.split('/').pop();
      return {
        source: 'Vimeo',
        displayName: videoId ? `Vimeo video (${videoId})` : 'Vimeo video',
      };
    }

    if (
      lowerHostname.includes('facebook.com') ||
      lowerHostname.includes('fb.watch')
    ) {
      return {
        source: 'Facebook',
        displayName: 'Facebook video',
      };
    }

    if (lowerHostname.includes('instagram.com')) {
      return {
        source: 'Instagram',
        displayName: 'Instagram video',
      };
    }

    if (lowerHostname.includes('linkedin.com')) {
      return {
        source: 'LinkedIn',
        displayName: 'LinkedIn video',
      };
    }

    if (
      lowerHostname.includes('twitter.com') ||
      lowerHostname.includes('x.com')
    ) {
      return {
        source: 'Twitter/X',
        displayName: 'Twitter/X video',
      };
    }

    // Handle direct file URLs or cloud storage
    if (
      lowerHostname.includes('storage.googleapis.com') ||
      lowerHostname.includes('s3.amazonaws.com') ||
      lowerHostname.includes('amazonaws.com')
    ) {
      // For cloud storage, use generic user-friendly terms
      return {
        source: 'ScreenApp',
        displayName: 'recording',
      };
    }

    // Handle other file-like URLs
    const fileExtensionMatch = pathname.match(/\.([a-zA-Z0-9]+)(\?|$)/);
    if (fileExtensionMatch) {
      const extension = fileExtensionMatch[1].toLowerCase();
      const videoExtensions = [
        'mp4',
        'mov',
        'avi',
        'wmv',
        'flv',
        'mkv',
        'webm',
      ];
      const audioExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];

      if (
        videoExtensions.includes(extension) ||
        audioExtensions.includes(extension)
      ) {
        // Use user-friendly terms for media files instead of technical filenames
        const mediaType = videoExtensions.includes(extension)
          ? 'recording'
          : 'audio recording';
        return {
          source: hostname,
          displayName: mediaType,
        };
      }
    }

    // Fallback for other URLs
    return {
      source: hostname,
      displayName: `Content from ${hostname}`,
    };
  } catch {
    // If URL parsing fails, try to extract some basic info
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];

    if (lastPart && lastPart.includes('.')) {
      return {
        source: 'External Source',
        displayName: lastPart.split('?')[0],
      };
    }

    return {
      source: 'External Source',
      displayName: 'Imported content',
    };
  }
};

// Store import URLs temporarily until backend includes them
const importUrlCache: Map<string, string> = new Map();

// Export function to get cached import URL
export const getCachedImportUrl = (fileId: string): string | undefined => {
  return importUrlCache.get(fileId);
};

const handleImportUpdate = async (
  payload: UpdateMessageMap['import']['payload'],
) => {
  const { dispatch } = store;
  const { teamId, parentId, fileId, status, error, url } = payload;

  // Cache the URL for this import
  if (url && fileId) {
    importUrlCache.set(fileId, url);
    console.log('Cached import URL:', { fileId, url });
  }

  // Add notification based on status
  let title;
  let message;
  let link;

  if (status === 'failed') {
    // Extract user-friendly information from the URL (with fallback for undefined)
    const { displayName } = extractUserFriendlyUrlInfo(url || 'Unknown source');
    title = 'Import Failed';
    message = `Failed to import ${displayName}: ${error || 'Unknown error'}`;
    link = undefined;
    // Clean up cache on failure
    if (fileId) {
      importUrlCache.delete(fileId);
    }
  } else if (status === 'completed') {
    // Extract user-friendly information from the URL (with fallback for undefined)
    const { displayName } = extractUserFriendlyUrlInfo(url || 'Unknown source');
    title = 'Import Complete';
    message = `Successfully imported ${displayName}`;
    link = getFileUrl({ teamId, parentId, fileId, withBasePath: true });
    // Clean up cache on completion
    if (fileId) {
      importUrlCache.delete(fileId);
    }
  } else {
    // Comment out processing notification - users don't need to see this
    // title = 'Processing Import';
    // message = `Processing ${displayName} from ${source}...`;
    // link = undefined;

    // Don't show processing notifications, just return early
    dispatch(triggerFileListRefresh());
    return;
  }

  const notificationData = {
    id: fileId,
    type: 'import' as const,
    title,
    message,
    link,
    read: false,
    timestamp: Date.now(),
  };

  // Use updateNotification to replace any existing notification for this file
  // This prevents multiple notifications for the same import
  dispatch(updateNotification(notificationData));

  // Trigger a refresh of the file list
  dispatch(triggerFileListRefresh());
};

const handleFileUpdate = async (
  payload: UpdateMessageMap['fileUpdate']['payload'],
) => {
  const { dispatch, getState } = store;
  const state = getState() as RootState;
  const { teamId, parentId, fileId, name } = payload;
  const { _id } = state?.selectedVideo?.file;

  // If this is the currently selected file, fetch the updated file without changing loading state
  if (fileId === _id) {
    dispatch(fetchFileUpdate(fileId));
  } else {
    // Update the file in the library
    dispatch(
      updateFile({
        _id: fileId,
        name,
      }),
    );

    // Check if a notification for this file already exists
    const existingNotification = state.dashboard.notifications.find(
      (notification) => notification.id === fileId,
    );

    // Create an engaging, user-friendly notification
    const notificationTitle = name ? `${name}` : 'Your recording is ready';
    const notificationMessage = name
      ? 'Transcription and AI complete'
      : 'Processing complete';

    // Use updateNotification to either create or update the notification for this file
    // This prevents duplicate notifications for the same file across different processing stages
    dispatch(
      updateNotification({
        id: fileId,
        type: 'transcription',
        title: notificationTitle,
        message: notificationMessage,
        link: getFileUrl({ teamId, parentId, fileId, withBasePath: true }),
        read: existingNotification?.read ?? false,
        timestamp: existingNotification?.timestamp ?? Date.now(),
      }),
    );
  }
};

const handleJobUpdate = (payload: UpdateMessageMap['jobUpdate']['payload']) => {
  const { dispatch, getState } = store;
  const state = getState() as RootState;
  const { _id } = state?.selectedVideo?.file;

  if (_id === payload.recordingId && payload.job === 'transcribe') {
    dispatch(updateJobStatus({ ...payload, job: 'transcribe' }));
  }
};

const updateHandler = (update: UpdateMessageMap[keyof UpdateMessageMap]) => {
  console.log('socket update,', update);
  try {
    const { type, payload } = update;
    const { dispatch } = store;
    logger.info(`socket: Received ${type} update`, payload);
    switch (type) {
      case 'usage':
        dispatch(setUsage(payload));
        break;
      case 'file':
        handleFileUpdate(payload).catch((error) => {
          logger.error('socket: Error handling file update', error);
        });
        break;
      case 'job':
        handleJobUpdate(payload);
        break;
      case 'rag':
        handleRagUpdate(payload);
        logger.info('socket: Received RAG update');
        break;
      case 'import':
        handleImportUpdate(payload);
        break;
      default:
        logger.warn(`socket: Unknown update type ${type}`);
        break;
    }
  } catch (error) {
    logger.error('socket: Error handling update', error);
  }
};

export default updateHandler;
